<template>
  <form @submit.prevent="submit" :class="errorClass">
    <button @click="prevStep" class='btn' v-if="step !== 1">Go back</button>
    <transition name="fade" mode="in-out">
      <rp-patient-lookup
        class="inner-form"
        v-if="lookupPatientStep"
        v-model="patient"
        userType="patient"
        @submit="lookupUser"/>
    </transition>

    <transition name="fade" mode="in-out">
      <rp-patient-form
        class="inner-form"
        v-if="patientInfoStep"
        @submit="updatePatient"
        :genders="genders"
        :disabled="disabled"
        :locales="locales"
        :engagementTypes="engagementTypes"
        surgeryStep
        v-model="patient"/>
    </transition>

    <rp-operation-form
      ref="opform"
      class="inner-form"
      v-if="operationInfoStep"
      :hospitals="hospitals"
      :surgeons="surgeons"
      :iolModels="iolModels"
      :leftIolPowers="iolPowers.left"
      :leftDuetIolPowers="iolPowers.leftDUET"
      :rightIolPowers="iolPowers.right"
      :rightDuetIolPowers="iolPowers.rightDUET"
      :leftCylPowers="cylPowers.left"
      :leftDuetCylPowers="cylPowers.leftDUET"
      :rightCylPowers="cylPowers.right"
      :rightDuetCylPowers="cylPowers.rightDUET"
      :operationIsNew=true
      @submit="operationSubmit"
      @getHospitals="getHospitals"
      @getSurgeons="getSurgeons"
      @getIolModels="getIolModels"
      @getIolPowers="getIolPowers"
      @getQuestionnaires="getQuestionnaires"
      v-model="operation"/>
  </form>
</template>

<script>
  import {validationMixin} from 'vuelidate'
  import {email, required} from 'vuelidate/lib/validators'
  import {errorMessages} from '../../../api/error_messages'
  import {getPatientByFilter, getUsersByRole, createPatient, getAssociatedSurgeons, getUserHospitals, getDefaultHospital} from '../../../api/users'
  import {getIolModels, getIolPowers} from '../../../api/iols'
  import {getLocales} from '../../../api/locales'
  import {getEngagementTypes} from '../../../api/engagement_types'

  import form from '../../../mixins/form'

  import Button from '../inputs/Button'
  import Password from '../inputs/PasswordInput'
  import TextInput from '../inputs/TextInput'
  import PatientForm from '../forms/Patient'
  import PatientLookup from '../forms/UserLookup'
  import OperationForm from '../forms/Operation'

  import {mapGetters} from 'vuex'

  /**
   * State machine to show correct form for operation setup process
   *
   * 3 steps:
   *
   * 1, lookup user by email
   * 2, user form
   * 3, operation details form
   *
   * value is operation object
   *
   * if no user in  operation (value prop) go to step 1
   *
   * on submitting user lookup, go to step 2, user will be either a blank user
   * or the user found in lookup
   *
   * after setting user, go to operation details form
   *
   * on submitting operation details, save operation
   *
   * if value (operation) has a user, start on step 2 with user info pre filled
   */

  const LOOKUP_PATIENT_STEP = 1
  const PATIENT_INFO_STEP = 2
  const OPERATION_INFO_STEP = 3

  export default {
    name: 'OperationMultiForm',
    mixins: [validationMixin, form],

    components: {
      'rp-button': Button,
      'rp-password': Password,
      'rp-textinput': TextInput,
      'rp-patient-form': PatientForm,
      'rp-patient-lookup': PatientLookup,
      'rp-operation-form': OperationForm
    },

    props: {patientEmail: ''},

    data() {
      return {
        step: 1,
        disabled: false,
        locales: [],
        engagementTypes: [],
        newPatient: {},
        patient: {
          patient_id: '',
          id: '',
          email: '',
          first_name: '',
          last_name: '',
          locale: '',
          yob: '',
          gender: '',
          phone: '',
          engagement_type_id: ''
        },

        operation: {
          surgeon_id: '',
          performed_at: '',
          operation_iols: [],
          hospital_id: '',
          patient_id: '',
          complex_surgery: false,
          blended_vision: false,
          aeon_drops_provided: false,
          welcome_email: true,
          leftEye: '',
          iol_model_left: '',
          iol_power_left: '',
          iol_power_left_cyl: '',
          iol_model_left_duet: '',
          iol_power_left_duet: '',
          iol_power_left_cyl_duet: '',
          rightEye: '',
          iol_model_right: '',
          iol_power_right: '',
          iol_power_right_cyl: '',
          iol_model_right_duet: '',
          iol_power_right_duet: '',
          iol_power_right_cyl_duet: '',
          questionnaire_answers: [],
        },

        iolModels: [],
        iolPowers: {
          left: [],
          right: [],
          leftDUET: [],
          rightDUET: []
        },
        cylPowers: {
          left: [],
          right: [],
          leftDUET: [],
          rightDUET: []
        },
        hospitals: [],
        surgeons: [],

        genders: [
          {'label': 'Male', 'value': 'M'},
          {'label': 'Female', 'value': 'F'},
          {'label': 'Prefer not to say', 'value': 'O'}
        ]
      }
    },

    mounted() {
      this.initStep()
      this.getLocales()
      this.getEngagementTypes()
      if (this.patientEmail) {
        this.lookupUser({filter: this.patientEmail})
      }
      this.setOperationSurgeonIfUserIsSurgeon()
    },

    computed: {
      ...mapGetters(['authUser']),
      lookupPatientStep() {
        return this.step === LOOKUP_PATIENT_STEP
      },

      patientPayload() {
        return {
          patient: {
            email: this.patient.email,
            locale_id: this.patient.locale,
            patient_id: this.patient.patient_id,
            created_by: this.authUser.id,
            profile: {
              'first_name': this.patient.first_name,
              'last_name': this.patient.last_name,
              gender: this.patient.gender,
              yob: this.patient.yob,
              phone: this.patient.phone,
              engagement_type_id: this.patient.engagement_type_id,
            }
          }
        }
      },

      patientInfoStep() {
        return this.step === PATIENT_INFO_STEP
      },

      operationInfoStep() {
        this.resource.hospital_id = this.operation.hospital_id
        this.resource.surgeon_id = this.operation.surgeon_id
        this.resource.performed_at = this.operation.performed_at + 'T12:00:00Z'
        this.resource.complex_surgery = this.operation.complex_surgery
        this.resource.blended_vision = this.operation.blended_vision
        this.resource.aeon_drops_provided = this.operation.aeon_drops_provided
        this.resource.welcome_email = this.operation.welcome_email
        return this.step === OPERATION_INFO_STEP
      },

      isInvalid() {
        return this.$v.$invalid
      },

      patientInfo: {
        get() {
          return this.resource.patient_id
        },

        set(value) {
          return value
        }
      },

      operationExists() {
        return !!this.resource.id
      },

      patientExists() {
        return !!this.resource.patient_id
      },

      userIsSurgeon() {
        return this.authUser.roles.reduce((acc, curr) => {
          if(acc) return true
          return curr.name === 'surgeon'
        }, false)
      }
    },

    validations: {
      resource: {
        email: {required, email},
        password: {required}
      }
    },

    methods: {

      getQuestionnaires(answers) {
        this.operation.questionnaire_answers = answers
        this.value.questionnaire_ids = answers
        
      },

      getLocales() {
        getLocales()
          .then(({data}) => {
            this.locales = data
          })
          .catch(() => {
          })
      },

      getEngagementTypes() {
        getEngagementTypes()
          .then(({ data }) => {
            this.engagementTypes = data;
          })
          .catch(() => {
          });
      },

      async operationSubmit() {
        this.setPresetIols()
        this.$emit('submit', this.value)
      },

      setPresetIols() {
        if (this.operation.iol_model_left) {
          this.resource.operation_iols.push(this.operationIolCreator(
            this.operation.iol_power_left, this.operation.iol_power_left_cyl, 'L', this.operation.iol_model_left
          ))
        }
        
        if (this.operation.iol_model_left_duet) {
          this.resource.operation_iols.push(this.operationIolCreator(
            this.operation.iol_power_left_duet, this.operation.iol_power_left_cyl_duet, 'L', this.operation.iol_model_left_duet
          ))
        }

        if (this.operation.iol_model_right) {
          this.resource.operation_iols.push(this.operationIolCreator(
            this.operation.iol_power_right, this.operation.iol_power_right_cyl, 'R', this.operation.iol_model_right
          ))
        }

        if (this.operation.iol_model_right_duet) {
          this.resource.operation_iols.push(this.operationIolCreator(
            this.operation.iol_power_right_duet, this.operation.iol_power_right_cyl_duet, 'R', this.operation.iol_model_right_duet
          ))
        }
      },

      operationIolCreator(power, powerCyl, eye, modelId) {
        let powers = []
        if (power) powers.push(power)
        if (powerCyl) powers.push(powerCyl)
        return {
          power: powers,
          eye: eye,
          iol_id: modelId
        }
      },

      getIolModels(isPlus) {
        getIolModels()
          .then(({data}) => {
            this.iolModels = data
          })
          .catch(({data}) => {
          })
      },

      getIolPowers(iolParams) {
        getIolPowers(iolParams)
          .then(({data}) => {
            let powers = data.powers
            if (powers === undefined) {powers = []}
            this.iolPowers[iolParams.type] = powers.filter((power) => {
              return !power.includes('(Cyl)')
            })

            this.cylPowers[iolParams.type] = powers.filter((power) => {
              return power.includes('(Cyl)')
            })

            if (this.cylPowers[iolParams.type].length > 0) {
              this.cylPowers[iolParams.type].unshift("Not Specified")
            }
          })
      },

      validateEmail(email) {
        const re = /\S+@\S+\.\S+/
        return re.test(email)
      },

      lookupUser(user) {
        // TODO: not catching if there's no patient in response
        getPatientByFilter(encodeURIComponent(user.filter.toLowerCase()))
          .then((result) => {
            const isEmail = this.validateEmail(user.filter)

            const mappedPatient = result.data.map((h) => {
              return {
                patient_id: h.patient_id,
                email: h.email || user.email,
                id: h.id,
                locale: h.locale.id,
                first_name: h.profile.first_name,
                last_name: h.profile.last_name,
                yob: h.profile.yob,
                gender: h.profile.gender,
                phone: h.profile.phone,
                engagement_type_id: h.profile.engagement_type_id,
              }
            })

            if (!mappedPatient[0]) {
              this.patient = {
                email: isEmail ? user.filter : '',
                patient_id: isEmail ? '' : user.filter 
              }
              this.disabled = false
            } else {
              this.patient = mappedPatient[0]
              this.resource.patient_id = this.patient.id
              this.disabled = true
            }

            this.loadPatientInfo()
          })
          .catch(() => {
          })
      },

      async getHospitals(userId) {
        if(!userId) {
          this.authUser.id
        }

        const defaultHospitalData = await getDefaultHospital({userId: userId})
        const defaultHospitalId = (defaultHospitalData.data) ? defaultHospitalData.data.id : ''
        getUserHospitals({id: userId}).then(({data}) => {
          this.hospitals = data
          this.$nextTick(() => {
            this.$refs.opform.onHospitalsRefreshed(defaultHospitalId)
          })
        })
        .catch(() => {
        })
      },

      getSurgeons() {
        const userId = this.authUser.id

        getAssociatedSurgeons({userId}).then(({data}) => {
          this.surgeons = data
        })
        .catch(() => {
        })
      },

      updatePatient() {
        if (!this.resource.patient_id) {
          createPatient(this.patientPayload)
            .then(({data}) => {
              this.resource.patient_id = data.id
              this.disabled = true
              this.setStep(OPERATION_INFO_STEP)

            })
            .catch(({response}) => {
              // TODO: map errors to flat structure for form
              this.errors = response.errors
            })
        }
        else {
          this.setStep(OPERATION_INFO_STEP)
        }
      },

      initStep() {
        if(!this.patientExists) {
          /**
           * Show patient look up form if patient not already selected
           */
          this.setStep(LOOKUP_PATIENT_STEP)
        } else {
          this.setStep(PATIENT_INFO_STEP)
        }
      },

      setStep(step) {
        this.step = step
        this.scrollToTop()
      },

      prevStep() {
        if(this.step > 1) {
          this.step = this.step - 1
        }
      },

      loadPatientInfo() {
        this.setStep(PATIENT_INFO_STEP)
      },

      setOperationSurgeonIfUserIsSurgeon() {
        if(this.userIsSurgeon) {
          this.operation.surgeon_id = this.authUser.id
        }
      },

      scrollToTop() {
        window.scrollTo(0, 0)
      }
    }
  }
</script>



// WEBPACK FOOTER //
// ../raypro_ui/src/components/ui/forms/OperationMultistep.vue